/* Country defaults use Shopify's first-party GeoIP discovery, never browser language. */
(() => {
  'use strict';
  const languageByCountry = {US: 'en', AE: 'en', FR: 'fr', ES: 'es'};
  const PREF = 'levelup_region_choice_v2';
  const GEO = 'levelup_detected_country_v2';
  const ATTEMPT = 'levelup_region_attempt_v2';
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const currentLanguage = () => document.documentElement.lang.split('-')[0];
  const read = (storage, key) => { try { return JSON.parse(storage.getItem(key)); } catch (_) { return null; } };
  const write = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const remove = (storage, key) => { try { storage.removeItem(key); } catch (_) {} };
  const storage = type => { try { return window[type]; } catch (_) { return {getItem: () => null, setItem() {}, removeItem() {}}; } };
  const local = storage('localStorage'), session = storage('sessionStorage');
  let detectedCountry = '', interacted = false;

  function init() {
    const countryForm = document.getElementById('LevelUpCountryForm');
    const languageForm = document.getElementById('LevelUpLanguageForm');
    const countrySelect = document.getElementById('LevelUpCountry');
    const autoButton = document.querySelector('[data-region-auto]');
    const status = document.querySelector('[data-region-status]');
    if (!countryForm || !languageForm || !countrySelect) return;
    const supportedCountries = new Set(Array.from(countrySelect.options, option => option.value));
    const supportedLanguages = new Set(Array.from(languageForm.querySelectorAll('[name="language_code"]'), button => button.value));
    const currentCountry = () => window.LevelUpRegion?.country || countrySelect.value;
    const text = {
      fr: {detected: 'Pays détecté : ', unavailable: 'Détection indisponible. Choisissez votre pays ci-dessus.', checking: 'Détection de votre pays…'},
      en: {detected: 'Detected country: ', unavailable: 'Detection unavailable. Choose your country above.', checking: 'Detecting your country…'},
      es: {detected: 'País detectado: ', unavailable: 'Detección no disponible. Elige tu país arriba.', checking: 'Detectando tu país…'},
      ar: {detected: 'البلد المكتشف: ', unavailable: 'تعذّر تحديد البلد. اختر بلدك أعلاه.', checking: 'جارٍ تحديد بلدك…'}
    }[currentLanguage()] || {detected: 'Detected country: ', unavailable: 'Choose your country above.', checking: 'Detecting your country…'};
    const showStatus = value => { if (status) { status.textContent = value; status.hidden = !value; } };
    const input = (form, name, value) => {
      let field = form.querySelector('input[name="' + name + '"]');
      if (!field) { field = document.createElement('input'); field.type = 'hidden'; field.name = name; form.appendChild(field); }
      field.value = value;
    };
    const returnPath = (language, automatic) => {
      const url = new URL(location.href);
      const base = url.pathname.replace(/^\/(?:en|es|ar|fr)(?=\/|$)/, '') || '/';
      url.pathname = (language === 'fr' ? '' : '/' + language) + base;
      url.searchParams.delete('_lu_localized');
      if (automatic) url.searchParams.set('_lu_localized', '1');
      return url.pathname + url.search + url.hash;
    };
    function remember(country, language) {
      write(local, PREF, {country, language, detectedCountry, at: Date.now()});
      remove(session, ATTEMPT);
    }
    function apply(country, language, automatic) {
      if (!supportedCountries.has(country) || !supportedLanguages.has(language)) return false;
      if (country === currentCountry() && language === currentLanguage()) return false;
      const key = country + ':' + language;
      const attempt = read(session, ATTEMPT);
      if (automatic && attempt?.key === key && Date.now() - attempt.at < 60000) return false;
      if (automatic) write(session, ATTEMPT, {key, at: Date.now()});
      const form = document.createElement('form');
      form.action = '/localization'; form.method = 'POST'; form.hidden = true;
      input(form, 'form_type', 'localization'); input(form, '_method', 'put');
      input(form, 'country_code', country); input(form, 'language_code', language);
      input(form, 'return_to', returnPath(language, automatic));
      document.body.appendChild(form); form.submit();
      return true;
    }
    countryForm.addEventListener('submit', () => {
      interacted = true;
      const country = countrySelect.value;
      /* A manual market choice changes prices, not the reader's language. */
      const language = currentLanguage();
      input(countryForm, 'language_code', language);
      input(countryForm, 'return_to', returnPath(language, false));
      remember(country, language);
    });
    languageForm.addEventListener('submit', event => {
      interacted = true;
      const language = event.submitter?.value;
      if (!supportedLanguages.has(language)) return;
      input(languageForm, 'country_code', currentCountry());
      input(languageForm, 'return_to', returnPath(language, false));
      remember(currentCountry(), language);
    });
    [countryForm, languageForm].forEach(form => form.addEventListener('change', () => { interacted = true; }));

    async function detect() {
      /* Always check the current connection: a VPN can change within this session.
         GEO only carries context across the immediate localization redirect. */
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 3500);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false', {credentials: 'same-origin', cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('Location discovery unavailable');
        const data = await response.json();
        const country = data.detected_values?.country?.handle;
        if (!/^[A-Z]{2}$/.test(country || '')) throw new Error('No detected country');
        write(session, GEO, {country, at: Date.now()});
        return country;
      } finally { clearTimeout(timer); }
    }
    async function route(fresh = false) {
      if (fresh) { interacted = false; remove(local, PREF); remove(session, ATTEMPT); showStatus(text.checking); }
      try {
        const country = await detect();
        detectedCountry = country;
        const label = Array.from(countrySelect.options).find(option => option.value === country)?.textContent.split(' · ')[0] || country;
        showStatus(text.detected + label);
        if (interacted || !supportedCountries.has(country)) return;
        const preference = read(local, PREF);
        const preferenceValid = preference && Date.now() - preference.at < MAX_AGE &&
          (!preference.detectedCountry || preference.detectedCountry === country) &&
          supportedCountries.has(preference.country) && supportedLanguages.has(preference.language);
        if (preferenceValid) {
          if (!preference.detectedCountry) write(local, PREF, {...preference, detectedCountry: country});
          apply(preference.country, preference.language, true); return;
        }
        if (preference) remove(local, PREF);
        const language = languageByCountry[country];
        if (language) apply(country, language, true);
      } catch (_) {
        showStatus(text.unavailable);
        /* Native Shopify country detection is a fallback; never guess from the browser language. */
        const preference = read(local, PREF);
        if (!interacted && !preference) {
          const country = currentCountry(), language = languageByCountry[country];
          if (language) apply(country, language, true);
        }
      }
    }
    autoButton?.addEventListener('click', () => route(true));
    const landed = new URL(location.href);
    if (landed.searchParams.has('_lu_localized')) {
      landed.searchParams.delete('_lu_localized');
      history.replaceState(history.state, '', landed.pathname + landed.search + landed.hash);
      const cached = read(session, GEO);
      detectedCountry = cached?.country || '';
      return;
    }
    if (!window.Shopify?.designMode && !/bot|crawler|spider|lighthouse/i.test(navigator.userAgent)) route();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
